/**
 * SOCTrajectoryChart - Displays battery SOC trajectories per vehicle
 *
 * Shows a representative week (7 days x 96 intervals = 672 data points)
 * from MPC simulation results. One line per vehicle group.
 */

import React, { useMemo } from 'react';
import { Box, Typography, useTheme } from '@mui/material';
import {
    LineChart,
    Line,
    XAxis,
    YAxis,
    Tooltip,
    ResponsiveContainer,
    CartesianGrid,
    ReferenceLine,
    Legend,
} from 'recharts';
import { vortxColors } from '@vortx/shared-ui';

const INTERVALS_PER_DAY = 96; // 15-min intervals
const WEEK_INTERVALS = 7 * INTERVALS_PER_DAY; // 672

const DAY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

// Vehicle line colors (reuse charger palette + extras)
const VEHICLE_COLORS = [
    '#3f6a64', '#b28c50', '#5a9a94', '#7a6a40',
    '#2d524d', '#d4b870', '#4a7a74', '#9a8050',
];
const VEHICLE_COLORS_DARK = [
    '#5fd4c4', '#d4a85a', '#4ab8a8', '#c0a060',
    '#38a898', '#e8c080', '#60c0b0', '#b09050',
];

/**
 * Format interval index to day + time string
 */
function formatIntervalTooltip(index) {
    const day = Math.floor(index / INTERVALS_PER_DAY);
    const intervalInDay = index % INTERVALS_PER_DAY;
    const hour = Math.floor(intervalInDay / 4);
    const min = (intervalInDay % 4) * 15;
    const period = hour < 12 ? 'AM' : 'PM';
    const h = hour % 12 || 12;
    return `${DAY_LABELS[day] || `Day ${day}`} ${h}:${String(min).padStart(2, '0')} ${period}`;
}

/**
 * SOCTrajectoryChart Component
 *
 * @param {Object} props
 * @param {Object} props.socTrajectories - { vehicle_id: [float] } from MPC result
 * @param {number} props.targetSoc - Target SOC % for reference line (e.g. 95)
 * @param {number} props.minSoc - Minimum SOC floor % for reference line (e.g. 20)
 * @param {Array} props.fleet - Fleet array to get vehicle names
 * @param {number} props.height - Chart height in pixels
 */
function SOCTrajectoryChart({
    socTrajectories = {},
    targetSoc = 95,
    minSoc = 20,
    fleet = [],
    height = 350,
}) {
    const theme = useTheme();
    const isDark = theme.palette.mode === 'dark';
    const colors = isDark ? VEHICLE_COLORS_DARK : VEHICLE_COLORS;

    const vehicleIds = Object.keys(socTrajectories);

    // Build chart data: slice first week (672 intervals)
    const chartData = useMemo(() => {
        if (vehicleIds.length === 0) return [];

        const points = [];
        for (let i = 0; i < WEEK_INTERVALS; i++) {
            const entry = { index: i };
            for (const vid of vehicleIds) {
                const soc = socTrajectories[vid];
                entry[vid] = soc && i < soc.length ? Math.round(soc[i] * 10) / 10 : 0;
            }
            points.push(entry);
        }
        return points;
    }, [socTrajectories, vehicleIds]);

    // Build vehicle name map from fleet
    const vehicleNames = useMemo(() => {
        const names = {};
        for (const v of fleet) {
            const id = v.vehicle_id || v.id;
            if (id) {
                names[id] = v.vehicle_name || v.route_name || id;
            }
        }
        return names;
    }, [fleet]);

    // X-axis ticks: one per day boundary
    const dayTicks = useMemo(() => {
        return DAY_LABELS.map((_, i) => i * INTERVALS_PER_DAY);
    }, []);

    const axisColor = isDark ? 'rgba(255,255,255,0.5)' : theme.palette.text.secondary;

    if (vehicleIds.length === 0) {
        return (
            <Box sx={{ height, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Typography color="text.secondary">
                    No SOC data available. Run a simulation to see trajectories.
                </Typography>
            </Box>
        );
    }

    return (
        <Box sx={{ width: '100%', height }}>
            <ResponsiveContainer>
                <LineChart data={chartData} margin={{ top: 10, right: 12, left: 0, bottom: 0 }}>
                    <CartesianGrid
                        strokeDasharray="3 3"
                        vertical={false}
                        stroke={isDark ? 'rgba(255,255,255,0.08)' : '#eee'}
                    />
                    <XAxis
                        dataKey="index"
                        type="number"
                        domain={[0, WEEK_INTERVALS - 1]}
                        ticks={dayTicks}
                        tickFormatter={(val) => DAY_LABELS[Math.floor(val / INTERVALS_PER_DAY)] || ''}
                        stroke={axisColor}
                        style={{ fontSize: 11 }}
                    />
                    <YAxis
                        domain={[0, 100]}
                        ticks={[0, 20, 40, 60, 80, 100]}
                        stroke={axisColor}
                        style={{ fontSize: 11 }}
                        width={40}
                        tickFormatter={(val) => `${val}%`}
                        label={{
                            value: 'SOC',
                            angle: -90,
                            position: 'insideLeft',
                            style: { fill: axisColor, fontSize: 11 },
                        }}
                    />
                    <Tooltip
                        labelFormatter={formatIntervalTooltip}
                        formatter={(value, name) => [
                            `${value}%`,
                            vehicleNames[name] || name,
                        ]}
                        contentStyle={{
                            borderRadius: 8,
                            border: isDark ? '1px solid rgba(255,255,255,0.1)' : 'none',
                            boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
                            backgroundColor: theme.palette.background.paper,
                        }}
                    />

                    {/* Target SOC reference line */}
                    {targetSoc && (
                        <ReferenceLine
                            y={targetSoc}
                            stroke={vortxColors.gold}
                            strokeDasharray="5 5"
                            label={{
                                value: `Target ${targetSoc}%`,
                                position: 'right',
                                fill: vortxColors.gold,
                                fontSize: 10,
                            }}
                        />
                    )}

                    {/* Min SOC floor reference line */}
                    {minSoc && (
                        <ReferenceLine
                            y={minSoc}
                            stroke="#ff6b6b"
                            strokeDasharray="3 3"
                            label={{
                                value: `Min ${minSoc}%`,
                                position: 'right',
                                fill: '#ff6b6b',
                                fontSize: 10,
                            }}
                        />
                    )}

                    {/* One line per vehicle */}
                    {vehicleIds.map((vid, i) => (
                        <Line
                            key={vid}
                            type="monotone"
                            dataKey={vid}
                            name={vid}
                            stroke={colors[i % colors.length]}
                            strokeWidth={1.5}
                            dot={false}
                            isAnimationActive={false}
                        />
                    ))}

                    <Legend
                        formatter={(value) => vehicleNames[value] || value}
                        wrapperStyle={{ fontSize: 12 }}
                    />
                </LineChart>
            </ResponsiveContainer>
        </Box>
    );
}

export default SOCTrajectoryChart;
